import { Centrifuge, Subscription } from 'centrifuge'
import { eventsApi } from '../api/events.api'

/**
 * Per-(run, node_execution) Centrifugo client.
 *
 * Centrifugo v5 tokens carry an exact-match `channels` claim — no
 * wildcards. So each `(runId, nodeExecutionId)` pair gets its own
 * Centrifuge instance with a token scoped to that one channel. The
 * scan-detail node-detail modal only views one node at a time, so cache
 * size stays bounded; navigating between nodes creates fresh instances
 * and tears down the old one via `disconnectClient`.
 *
 * Token refresh: `centrifuge` calls `getToken` on connect AND when the
 * existing token is ~30s from expiry. We re-fetch with the same
 * (runId, nodeExecutionId) so the channel claim is preserved.
 *
 * Bootstrap: Centrifuge 5 has no `setEndpoint`; the WSS URL must be known
 * at construction time. We fetch the token+url once eagerly, then reuse.
 */
const clients = new Map<string, Promise<Centrifuge>>()

function key(runId: string, nodeExecutionId: string): string {
  return `${runId}::${nodeExecutionId}`
}

export function getCentrifugoClient(
  runId: string,
  nodeExecutionId: string,
): Promise<Centrifuge> {
  const k = key(runId, nodeExecutionId)
  const cached = clients.get(k)
  if (cached) return cached

  const p = (async () => {
    const { token, url } = await eventsApi.centrifugoToken(
      runId,
      nodeExecutionId,
    )
    const cent = new Centrifuge(url, {
      token,
      getToken: async () => {
        const fresh = await eventsApi.centrifugoToken(runId, nodeExecutionId)
        return fresh.token
      },
      debug: false,
    })
    cent.connect()
    return cent
  })()

  clients.set(k, p)
  return p
}

type PublicationHandler = (data: unknown) => void

interface OurSubscription {
  cacheKey: string
  sub: Subscription
  handler: (ctx: { data: unknown }) => void
}

export async function subscribeChannel(
  runId: string,
  nodeExecutionId: string,
  channel: string,
  onPublication: PublicationHandler,
): Promise<OurSubscription> {
  const cent = await getCentrifugoClient(runId, nodeExecutionId)
  let sub = cent.getSubscription(channel)
  if (!sub) {
    sub = cent.newSubscription(channel)
    sub.subscribe()
  }
  const handler = (ctx: { data: unknown }) => onPublication(ctx.data)
  sub.on('publication', handler)
  return { cacheKey: key(runId, nodeExecutionId), sub, handler }
}

export function unsubscribeChannel(s: OurSubscription): void {
  s.sub.off('publication', s.handler)
  const remaining =
    typeof (s.sub as unknown as { listenerCount?: (e: string) => number })
      .listenerCount === 'function'
      ? (
          s.sub as unknown as { listenerCount: (e: string) => number }
        ).listenerCount('publication')
      : 0
  if (remaining === 0) {
    s.sub.unsubscribe()
    void disconnectClientByKey(s.cacheKey)
  }
}

async function disconnectClientByKey(k: string): Promise<void> {
  const cached = clients.get(k)
  if (!cached) return
  clients.delete(k)
  try {
    const cent = await cached
    cent.disconnect()
  } catch {
    // Never finished initialising — nothing to do.
  }
}

/**
 * Explicit teardown for one (run, node) pair. Optional — `unsubscribeChannel`
 * will drop the client once its last listener is removed.
 */
export async function disconnectClient(
  runId: string,
  nodeExecutionId: string,
): Promise<void> {
  return disconnectClientByKey(key(runId, nodeExecutionId))
}
